import React, { useState } from 'react';
import {
  School,
  Users,
  Calendar,
  UserPlus,
  GraduationCap,
  Shield,
  Bell,
  CreditCard,
  Puzzle,
  LogOut,
  Save,
  RotateCcw,
} from 'lucide-react';
import { SchoolInformationSection } from './SchoolInformationSection';
import { ManageAdminsSection } from './ManageAdminsSection';
import { DatesHolidaysSection } from './DatesHolidaysSection';
import { InviteTeachersSection } from './InviteTeachersSection';
import { InviteStudentsSection } from './InviteStudentsSection';
import { NotificationsSection } from './NotificationsSection';
import { FinanceSettingsSection } from './FinanceSettingsSection';
import { IntegrationsSection } from './IntegrationsSection';

type SectionType =
  | 'school-info'
  | 'manage-admins'
  | 'dates-holidays'
  | 'invite-teachers'
  | 'invite-students'
  | 'security'
  | 'notifications'
  | 'finance'
  | 'integrations';

interface NavItem {
  id: SectionType;
  label: string;
  icon: React.ReactNode;
  subItems?: { id: string; label: string }[];
}

const navItems: NavItem[] = [
  {
    id: 'school-info',
    label: 'School Information',
    icon: <School className="w-4 h-4" />,
  },
  {
    id: 'manage-admins',
    label: 'Manage Admins',
    icon: <Users className="w-4 h-4" />,
  },
  {
    id: 'dates-holidays',
    label: 'Dates & Holidays',
    icon: <Calendar className="w-4 h-4" />,
  },
  {
    id: 'invite-teachers',
    label: 'Invite Teachers',
    icon: <UserPlus className="w-4 h-4" />,
    subItems: [
      { id: 'by-link', label: 'By Link' },
      { id: 'add-manually', label: 'Add Manually' },
    ],
  },
  {
    id: 'invite-students',
    label: 'Invite Students',
    icon: <GraduationCap className="w-4 h-4" />,
    subItems: [
      { id: 'by-link', label: 'By Link' },
      { id: 'add-manually', label: 'Add Manually' },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    icon: <Shield className="w-4 h-4" />,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <Bell className="w-4 h-4" />,
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: <CreditCard className="w-4 h-4" />,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: <Puzzle className="w-4 h-4" />,
  },
];

export function SettingsPage() {
  const [activeSection, setActiveSection] = useState<SectionType>('school-info');
  const [expandedItems, setExpandedItems] = useState<string[]>(['invite-teachers', 'invite-students']);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    console.log('Saving settings...');
  };

  const handleReset = () => {
    console.log('Resetting settings...');
  };

  return (
    <div className="flex gap-6 min-h-[calc(100vh-8rem)]">
      {/* Left Sidebar Navigation */}
      <div className="w-64 flex-shrink-0">
        <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <div key={item.id}>
                <button
                  onClick={() => {
                    setActiveSection(item.id);
                    if (item.subItems) {
                      toggleExpand(item.id);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeSection === item.id
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
                {item.subItems && expandedItems.includes(item.id) && (
                  <div className="ml-7 mt-1 space-y-1">
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.id}
                        onClick={() => setActiveSection(item.id)}
                        className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Bottom Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200 space-y-2">
            <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
            <button
              onClick={handleSave}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
            >
              <Save className="w-4 h-4" />
              Save & Exit
            </button>
            <button
              onClick={handleReset}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Settings
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {activeSection === 'school-info' && <SchoolInformationSection />}
        {activeSection === 'manage-admins' && <ManageAdminsSection />}
        {activeSection === 'dates-holidays' && <DatesHolidaysSection />}
        {activeSection === 'invite-teachers' && <InviteTeachersSection />}
        {activeSection === 'invite-students' && <InviteStudentsSection />}
        {activeSection === 'security' && <SecuritySection />}
        {activeSection === 'notifications' && <NotificationsSection />}
        {activeSection === 'finance' && <FinanceSettingsSection />}
        {activeSection === 'integrations' && <IntegrationsSection />}
      </div>
    </div>
  );
}

// Placeholder for Security section
function SecuritySection() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h2>
      <p className="text-gray-500">Security settings configuration coming soon.</p>
    </div>
  );
}
